import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';
import api, { formatApiErrorDetail } from '../lib/api';

export default function BookingPage() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [answers, setAnswers] = useState({});
  const [preferredDate, setPreferredDate] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, q] = await Promise.all([
          api.get(`/services/${serviceId}`),
          api.get(`/services/${serviceId}/questionnaire`),
        ]);
        setService(s.data);
        setQuestionnaire(q.data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || 'Service not found');
      } finally {
        setLoading(false);
      }
    })();
  }, [serviceId]);

  const onAnswer = (key, value) => setAnswers((prev) => ({ ...prev, [key]: value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const { data } = await api.post('/bookings', {
        service_id: serviceId,
        preferred_date: preferredDate || null,
        preferred_time: preferredTime || null,
        notes: notes || null,
        answers,
      });
      // If paid service, launch Razorpay checkout
      if (!data.is_free) {
        await launchRazorpay(data);
      } else {
        setSubmitted(data);
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const launchRazorpay = async (booking) => {
    try {
      const { data: order } = await api.post('/payments/create-order', {
        booking_id: booking.booking_id,
      });

      if (!window.Razorpay) {
        setError('Payment script failed to load. Please refresh and try again.');
        return;
      }

      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: 'Soul Aligned',
        description: booking.service_title,
        order_id: order.order_id,
        prefill: {
          name: order.user_name,
          email: order.user_email,
          contact: order.user_mobile || '',
        },
        notes: { booking_id: booking.booking_id },
        theme: { color: '#1a1a1a' },
        handler: async (resp) => {
          try {
            await api.post('/payments/verify', {
              booking_id: booking.booking_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setSubmitted({ ...booking, payment_status: 'paid', status: 'confirmed' });
          } catch (verErr) {
            setError(
              formatApiErrorDetail(verErr.response?.data?.detail) ||
                'Payment received but verification failed. Please contact support.'
            );
          }
        },
        modal: {
          ondismiss: async () => {
            await api.post(`/payments/mark-failed/${booking.booking_id}`).catch(() => {});
            setError('Payment cancelled. You can retry from your dashboard.');
          },
        },
      });
      rzp.on('payment.failed', async () => {
        await api.post(`/payments/mark-failed/${booking.booking_id}`).catch(() => {});
        setError('Payment failed. Please try a different method or retry.');
      });
      rzp.open();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || 'Could not start payment. Try again.');
    }
  };

  if (loading) {
    return (
      <div className="booking-page-loading" data-testid="booking-loading">
        <Loader2 className="spin" size={28} /> Loading…
      </div>
    );
  }

  if (!service) {
    return (
      <div className="booking-page" data-testid="booking-error-page">
        <div className="booking-card">
          <p className="auth-error">{error || 'Service not found'}</p>
          <Link to="/dashboard" className="btn-primary">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    const isPaid = submitted.payment_status === 'paid';
    return (
      <div className="booking-page" data-testid="booking-success">
        <div className="booking-card success">
          <CheckCircle size={56} className="success-icon" />
          <h1>
            {service.is_free
              ? 'Discovery call requested!'
              : isPaid
              ? 'Payment successful! 🎉'
              : 'Booking received!'}
          </h1>
          <p>
            {service.is_free
              ? "We've received your details. You'll get a confirmation with the call link within 24 hours."
              : isPaid
              ? `Your ${service.title} booking is confirmed. You'll receive session details within 24 hours.`
              : `Your ${service.title} booking is saved. We'll be in touch shortly.`}
          </p>
          <div className="booking-success-actions">
            <Link to="/dashboard" className="btn-primary btn-large" data-testid="back-to-dashboard">
              Go to my dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-page" data-testid="booking-page">
      <div className="booking-card">
        <Link to="/dashboard" className="auth-back" data-testid="back-to-dashboard-link">
          <ArrowLeft size={16} /> Back to dashboard
        </Link>

        <div className="booking-head">
          <h1>{service.title}</h1>
          <div className="booking-meta">
            <span>{service.duration}</span>
            <span className="dot">·</span>
            <span>{service.is_free ? 'Free' : `₹${service.price.toLocaleString('en-IN')}`}</span>
          </div>
          <p className="booking-desc">{service.description}</p>
          {questionnaire?.intro && <p className="booking-intro">{questionnaire.intro}</p>}
        </div>

        <form onSubmit={onSubmit} className="booking-form">
          {questionnaire?.questions?.map((q) => (
            <div key={q.key} className="auth-field">
              <label>
                {q.label} {q.required && <span className="req">*</span>}
              </label>
              {q.type === 'textarea' && (
                <textarea
                  value={answers[q.key] || ''}
                  onChange={(e) => onAnswer(q.key, e.target.value)}
                  required={q.required}
                  rows={4}
                  placeholder={q.placeholder}
                  data-testid={`q-${q.key}`}
                />
              )}
              {q.type === 'text' && (
                <input
                  type="text"
                  value={answers[q.key] || ''}
                  onChange={(e) => onAnswer(q.key, e.target.value)}
                  required={q.required}
                  placeholder={q.placeholder}
                  data-testid={`q-${q.key}`}
                />
              )}
              {q.type === 'select' && (
                <select
                  value={answers[q.key] || ''}
                  onChange={(e) => onAnswer(q.key, e.target.value)}
                  required={q.required}
                  data-testid={`q-${q.key}`}
                >
                  <option value="">Select…</option>
                  {q.options?.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              )}
            </div>
          ))}

          <div className="booking-grid-2">
            <div className="auth-field">
              <label>Preferred date (optional)</label>
              <input
                type="date"
                value={preferredDate}
                onChange={(e) => setPreferredDate(e.target.value)}
                data-testid="preferred-date"
              />
            </div>
            <div className="auth-field">
              <label>Preferred time (optional)</label>
              <input
                type="time"
                value={preferredTime}
                onChange={(e) => setPreferredTime(e.target.value)}
                data-testid="preferred-time"
              />
            </div>
          </div>

          <div className="auth-field">
            <label>Any other notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Anything else you'd like us to know"
              data-testid="booking-notes"
            />
          </div>

          {error && <div className="auth-error" data-testid="booking-error">{error}</div>}

          <button
            type="submit"
            disabled={submitting}
            className="btn-primary btn-large btn-full"
            data-testid="submit-booking"
          >
            {submitting
              ? 'Processing…'
              : service.is_free
              ? 'Confirm free discovery call'
              : `Pay ₹${service.price.toLocaleString('en-IN')} & Book`}
          </button>
          {!service.is_free && (
            <p className="booking-note">
              Secure payment via Razorpay. UPI, cards, netbanking, wallets all supported.
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
